import type { Thresholds } from '../../core/thresholds';
import type { DomainLevelCounts, WidaLevelKey } from '../Dashboard/domainBars';
import { WIDA_LEVEL_KEYS } from '../Dashboard/domainBars';
import { DOMAIN_KEYS, type RosterDomainKey, type RosterRow } from '../Roster/rosterRows';

const EM_DASH = '—';

export type GradeCount = { grade: string; count: number };

export type YearBucket = {
  label: string;
  count: number;
  isLtel: boolean;
  isApproaching: boolean;
};

export type TeacherCaseload = { teacher: string; students: number };

export type DomainDataQuality = {
  byDomain: { domain: RosterDomainKey; missing: number }[];
  studentsMissingAny: number;
};

function gradeRank(grade: string): number {
  if (grade === 'K') return 0;
  const numeric = /^(\d+)(st|nd|rd|th)$/.exec(grade);
  return numeric ? Number(numeric[1]) : Number.MAX_SAFE_INTEGER;
}

export function gradeDistribution(rows: RosterRow[]): GradeCount[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.grade === EM_DASH || row.grade.trim() === '') continue;
    counts.set(row.grade, (counts.get(row.grade) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([grade, count]) => ({ grade, count }))
    .sort((a, b) => gradeRank(a.grade) - gradeRank(b.grade) || a.grade.localeCompare(b.grade));
}

export function yearsInProgram(rows: RosterRow[], thresholds: Thresholds): YearBucket[] {
  const min = Math.max(1, thresholds.ltelMinYears);
  const buckets: YearBucket[] = [];
  for (let year = 1; year < min; year += 1) {
    buckets.push({
      label: `Yr ${year}`,
      count: 0,
      isLtel: false,
      isApproaching: year === min - 1,
    });
  }
  buckets.push({ label: `Yr ${min}+`, count: 0, isLtel: true, isApproaching: false });

  for (const row of rows) {
    const years = row.yearsInEl;
    if (years == null || years < 1) continue;
    const index = Math.min(years, min) - 1;
    buckets[index]!.count += 1;
  }
  return buckets;
}

export function approachingLtelCount(rows: RosterRow[], thresholds: Thresholds): number {
  const cusp = thresholds.ltelMinYears - 1;
  if (cusp < 1) return 0;
  return rows.filter((row) => row.yearsInEl === cusp).length;
}

function emptyLevelCounts(): DomainLevelCounts {
  return Object.fromEntries(WIDA_LEVEL_KEYS.map((key) => [key, 0])) as DomainLevelCounts;
}

function levelKeyFor(score: number): WidaLevelKey {
  const banded = Math.min(WIDA_LEVEL_KEYS.length, Math.max(1, Math.floor(score)));
  return WIDA_LEVEL_KEYS[banded - 1]!;
}

export function proficiencyProfile(rows: RosterRow[]): Record<RosterDomainKey, DomainLevelCounts> {
  const profile = Object.fromEntries(
    DOMAIN_KEYS.map((domain) => [domain, emptyLevelCounts()]),
  ) as Record<RosterDomainKey, DomainLevelCounts>;

  for (const row of rows) {
    for (const domain of DOMAIN_KEYS) {
      const score = row.domainPLs[domain];
      if (score == null) continue;
      profile[domain][levelKeyFor(score)] += 1;
    }
  }
  return profile;
}

export function teacherCaseloads(rows: RosterRow[]): TeacherCaseload[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const teacher of row.teachers) {
      const name = teacher.trim();
      if (name === '') continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([teacher, students]) => ({ teacher, students }))
    .sort((a, b) => b.students - a.students || a.teacher.localeCompare(b.teacher));
}

export function domainDataQuality(rows: RosterRow[]): DomainDataQuality {
  const byDomain = DOMAIN_KEYS.map((domain) => ({
    domain,
    missing: rows.filter((row) => row.missingDomains.includes(domain)).length,
  }));
  return {
    byDomain,
    studentsMissingAny: rows.filter((row) => row.missingDomains.length > 0).length,
  };
}
